import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import {
    ChangeDetectorRef,
    Component,
    ContentChild,
    DestroyRef,
    inject,
    Input,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import {
    BehaviorSubject,
    catchError,
    combineLatestWith,
    debounceTime,
    EMPTY,
    map,
    switchMap,
    tap,
    timer,
} from 'rxjs';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { Order_By } from 'src/gql-generated/generated';
import { NgIf, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatFormField, MatSuffix, MatError } from '@angular/material/form-field';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'smart-select',
    templateUrl: './smart-select.component.html',
    styleUrls: ['./smart-select.component.scss'],
    imports: [
        NgIf,
        MatFormField,
        MatSelect,
        ReactiveFormsModule,
        NgClass,
        MatIconButton,
        MatIcon,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        MatOption,
        NgTemplateOutlet,
        MatSuffix,
        MatError,
    ],
})
export class SmartSelectComponent {
    /**
     * Reference to template for mat-option that is optionally provided
     */
    @ContentChild('matOptionContent') matOptionContent: TemplateRef<any> | null = null;

    /**
     * Reference to virual scroll viewport
     */
    @ViewChild(CdkVirtualScrollViewport) scrollViewPort: CdkVirtualScrollViewport | null = null;

    /**
     * Reference to mat select component
     */
    @ViewChild(MatSelect) matSelect?: MatSelect;

    /**
     * Settings
     */
    @Input() settings?: SmartSelectSettings;

    /**
     * Control used to apply search on settings.query
     */
    searchControl = new FormControl('', { nonNullable: true });

    /**
     * True when awaiting response of settings.query
     */
    isLoading = false;

    /**
     * Cached results of settings.query; When user scrolls down (or presses load more) the cache is expanded with new items
     */
    cache: any[] = [];

    /**
     * Total number of items that could potentially be queried by settings.query. This is calculated via an aggergate
     * in the graphql query
     */
    total = 0;

    /**
     * The amount of items that are fetched with each query (graphql query limit)
     */
    pageSize = 10;

    /**
     * Current page to fetch in the query (used to calculate grapqhl query offset)
     */
    page = new BehaviorSubject<number>(0);

    /**
     * Fixed row height for each of the items in the virtual scroll
     */
    rowHeight = 48;

    /**
     * Max amount of rows to display before container starts overflowing and scroll bar becomes visible
     */
    maxRows = 5;

    /**
     * Automatically load more contenet when user scrolls to bottom of scroll container
     */
    loadMoreOnScroll = true;

    /**
     * Properties to filter items with. Value of searchControl must be included in this one of these columns.
     */
    searchProperties = ['name'];

    /**
     * Minimal amount of time (in ms) query should take to show data
     */
    private delayAmount = 300;

    /**
     * Destroy ref used in takeUntilDestroyed
     */
    private destroyRef = inject(DestroyRef);

    constructor(private snackbar: SnackbarService, private cd: ChangeDetectorRef, private sentry: SentryService) {
        // Subscribe to changes of searchControl to set page back to 0
        this.searchControl.valueChanges.pipe(
            tap(() => {
                this.isLoading = true;
            }),
            debounceTime(300),
            takeUntilDestroyed(),
        ).subscribe(() => {
            // Set delay amount to 0 because the searchControl already has an 300ms delay
            this.delayAmount = 0;
            this.page.next(0);
        });
    }

    ngOnChanges(): void {
        // Overwrite default values when provided in settings
        if (this.settings?.pageSize) {
            this.pageSize = this.settings.pageSize;
        }
        if (this.settings?.rowHeight) {
            this.rowHeight = this.settings.rowHeight;
        }
        if (this.settings?.maxRows) {
            this.maxRows = this.settings.maxRows;
        }
        if (this.settings?.loadMoreOnScroll !== undefined) {
            this.loadMoreOnScroll = this.settings.loadMoreOnScroll;
        }
        if (this.settings?.searchProperties) {
            this.searchProperties = this.settings.searchProperties;
        }

        // Create observable that emits when page changes (either more content needs to be loaded or search changed)
        this.page.asObservable()
            .pipe(
                // Set loading state to true
                tap(() => {
                    this.isLoading = true;
                }),

                // Run query provided in settings
                switchMap(() => this.settings?.query!({
                    limit: this.pageSize,
                    offset: this.pageSize * this.page.value,
                    where: {
                        _and: [
                            // Add fixedWhere
                            this.settings.fixedWhere || {},
                            {
                                // Apply filter on searchProperties
                                _or: this.searchProperties.map((searchProperty) => ({
                                    [searchProperty]: { _ilike: `%${this.searchControl.value.trim().toLowerCase()}%` },
                                })),
                            },
                        ],
                    },
                    // Order by searchProperties' first item
                    order_by: [{ [this.searchProperties[0]]: Order_By.Asc }],
                }, { fetchPolicy: 'no-cache' }).pipe(
                    catchError((error) => {
                        this.sentry.error(error);
                        this.isLoading = false;
                        this.snackbar.error(
                            this.settings?.queryErrorMessage || $localize`Could not search items`,
                        );
                        // Return EMPTY to keep outer observable alive
                        return EMPTY;
                    }),
                ).pipe(
                    // Make sure response takes at least 300ms to load
                    combineLatestWith(timer(this.delayAmount)),
                    tap(() => {
                        this.delayAmount = 300;
                    }),
                    map((x: any) => x[0]),
                )),
                // Set loading state to false
                tap(() => {
                    this.isLoading = false;
                }),
                takeUntilDestroyed(this.destroyRef),
            ).subscribe((data: any) => {
                // Get returned items
                const items: any[] = data.data[Object.keys(data.data)[0]];

                // Set total (aggregate)
                this.total = data.data[Object.keys(data.data)[1]].aggregate.count;

                // Insert items into cache
                items.forEach((item, index) => {
                    this.cache[this.pageSize * this.page.value + index] = item;
                });
                this.cache = this.cache.slice(0, this.pageSize * this.page.value + items.length);

                // Force change detector to apply latest changes
                this.cd.detectChanges();
            });
    }

    /**
     * Handles user opening mat select overlay
     */
    opened(): void {
        // Subscribe to changes in scrollport to detect user scrolling to bottom
        this.scrollViewPort?.elementScrolled()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const element = this.scrollViewPort?.elementRef.nativeElement;
                if (element) {
                    // Check if user reached bottom
                    const reachedBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1;

                    // Load next content if the setting is enabled, user is at bottom, data is not currently being loaded
                    // and there is still more data to load
                    if (this.loadMoreOnScroll && reachedBottom && !this.isLoading && this.cache.length !== this.total) {
                        this.page.next(this.page.value + 1);
                    }
                }
            });
    }

    /**
     * Load more content when user pressed "Load more" button
     * This button is only available when settings.loadMoreOnScroll is false
     */
    loadMore(): void {
        if (!this.isLoading && this.cache.length !== this.total) {
            this.isLoading = true;
            this.page.next(this.page.value + 1);
        }
    }

    /**
     * Checks if item should be disabled in mat option
     * @param item Item to check disabled status for
     * @returns True if item should be disabled
     */
    isDisabled(item: any): boolean {
        return !!this.settings?.disableFunction && this.settings.disableFunction(item);
    }

    /**
     * Handles what to do when user closes selection overlay.
     */
    closed(): void {
        // Reset search control
        this.searchControl.reset();
    }

    /**
     * Function that retuns true when two rows (objects) should be considered equal
     * @param val1 First value
     * @param val2 Second value
     * @returns True if val1 is equal to val2
     */
    compareWith(val1: any, val2: any): boolean {
        if (this.settings?.compareFunction) {
            return this.settings.compareFunction(val1, val2);
        }
        return (val1?.id && val2?.id) ? val1.id === val2.id : val1 === val2;
    }

    /**
     * Open mat select dropdown
     */
    open(): void {
        this.matSelect?.open();
    }
}

/**
 * Settings for a smart-select component
 */
export interface SmartSelectSettings {
    /**
     * Control used to track current value of select control
     */
    control: FormControl;
    /**
     * Query used to filter items and get the aggregate.
     */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    query: Function;
    /**
     * Properties to search on. Defaults to [`name`] if not set
     */
    searchProperties?: string[];
    /**
     * Compare function. Defaults to comparing the `id` property of both values (if they exist)
     * and comparing the entire value otherwise:
     *
     * `(val1?.id && val2?.id) ? val1.id === val2.id : val1 === val2`
     */
    compareFunction?: (val1: any, val2: any) => boolean;
    /**
     * Optional message to show in case items could not be fetched via apollo
     */
    queryErrorMessage?: string;
    /**
     * Message to show in the mat error below the form field
     */
    invalidControlErrorMessage?: string;
    /**
     * Amount of items to fetch for each 'page' (e.g. offset amount)
     */
    pageSize?: number;
    /**
     * Height of a single mat-option row. Defaults to 48px
     */
    rowHeight?: number;
    /**
     * Max amount of rows to show, defaults to 5
     */
    maxRows?: number;
    /**
     * Loads more items on scroll (defaults to true)
     */
    loadMoreOnScroll?: boolean;
    /**
     * Placeholder to show in mat select
     */
    placeholder?: string;
    /**
     * Fixed object that will always be added to the where clause of the search query
     */
    fixedWhere?: any;
    /**
     * Hide search bar when set to true
     */
    hideSearch?: boolean;
    /**
     * Hide clear button bar when set to true
     */
    hideClear?: boolean;
    /**
     * Optional function that can used to check if mat option should be disabled. Exposes the item to check for as input variable
     */
    disableFunction?: (item: any) => boolean;
}
